import { createSelector } from 'reselect';
import _get from 'lodash/get';
import { MOET_CONSTANTS } from 'moet-core';

import { getDecodedWidgetToken } from '../utils/widget';
import { BPC_BASE_PATH } from '../bpc/utils/constants';
import { ONBOARDING_TYPE } from 'moet-core/src/constants';

export const IS_BPC_APP = window.location.pathname.startsWith(BPC_BASE_PATH);

const { WIDGET_TYPE } = MOET_CONSTANTS;

export const selectQueryFromLocation = state => _get(state, 'router.location.query', {});

export const getWidgetRegionFromQueryParam = createSelector(selectQueryFromLocation, query =>
  _get(query, 'region', null),
);

export const getFrontendTypeFromQueryParam = createSelector(selectQueryFromLocation, query =>
  _get(query, 'frontend_type', null),
);

export const getWidgetCookieSafe = () => getDecodedWidgetToken() || {};

export const getMerchantIdFromWidgetCookie = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.mid,
);

export const getOnboardingTypeFromWidgetCookie = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.onboardingType,
);

export const getStatusFromWidgetCookie = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.status,
);

export const isWidget = createSelector(getWidgetCookieSafe, cookie =>
  // when the widget link is expired, there is no mid there
  IS_BPC_APP ? !!cookie.contractKrn : !!cookie.mid || !!cookie.token,
);

export const getWidgetTypeFromWidgetCookie = createSelector(
  getWidgetCookieSafe,
  isWidget,
  (cookie, isWidget) => (isWidget ? cookie.frontendType : null),
);

export const getWidgetTypeDefaultUnmanaged = createSelector(
  getWidgetCookieSafe,
  isWidget,
  (cookie, isWidget) => (isWidget ? cookie.frontendType : WIDGET_TYPE.UNMANAGED_KYC),
);

export const isSME = createSelector(getFrontendTypeFromQueryParam, widgetType =>
  [WIDGET_TYPE.MANAGED_SME_RISK, WIDGET_TYPE.MANAGED_SME_KYC].includes(widgetType),
);

export const hasInvalidToken = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.error && (cookie.error === 404 || cookie.error === 422),
);

export const hasInvalidFlowType = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.error && cookie.error === 400,
);

export const getCorrelationIdFromWidgetCookie = createSelector(
  getWidgetCookieSafe,
  cookie => cookie.correlationId,
);

export const isUnmanagedKyc = createSelector(
  isWidget,
  getFrontendTypeFromQueryParam,
  (isWidget, widgetType) => !isWidget || WIDGET_TYPE.UNMANAGED_KYC === widgetType,
);

export const isManagedKyc = createSelector(getFrontendTypeFromQueryParam, widgetType =>
  [WIDGET_TYPE.MANAGED_KYC, WIDGET_TYPE.MANAGED_SME_KYC].includes(widgetType),
);

export const isDocumentUploadWidget = createSelector(
  getFrontendTypeFromQueryParam,
  getWidgetTypeFromWidgetCookie,
  (widgetTypeFromQuery, widgetTypeFromCookie) =>
    widgetTypeFromQuery === WIDGET_TYPE.DOCUMENT_UPLOAD ||
    widgetTypeFromCookie === WIDGET_TYPE.DOCUMENT_UPLOAD,
);

export const isMerchantVerificationWidget = createSelector(
  getFrontendTypeFromQueryParam,
  getWidgetTypeFromWidgetCookie,
  (widgetTypeFromQuery, widgetTypeFromCookie) =>
    widgetTypeFromQuery === WIDGET_TYPE.MERCHANT_VERIFICATION ||
    widgetTypeFromCookie === WIDGET_TYPE.MERCHANT_VERIFICATION,
);

export const getOnboardingType = createSelector(
  getOnboardingTypeFromWidgetCookie,
  isWidget,
  (onboardingTypeFromWidgetCookie, isWidget) => {
    if (isWidget) {
      return onboardingTypeFromWidgetCookie;
    }

    return ONBOARDING_TYPE.UNMANAGED;
  },
);
